import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Heart, MessageSquare, Trash2, ChevronDown, LogIn } from 'lucide-react';
import { discussionsApi } from '@/api/endpoints';
import { getApiErrorMessage } from '@/api/client';
import type { DiscussionResponse } from '@/api/types';
import { useAuthStore } from '@/store/auth';
import { useToastStore } from '@/store/toast';
import { timeAgo } from '@/lib/format';
import { cn } from '@/lib/cn';
import type { Lang } from '@/types';

interface Props {
  slug: string;
}

export function DiscussionTab({ slug }: Props) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as Lang;
  const user = useAuthStore((s) => s.user);
  const push = useToastStore((s) => s.push);

  const [discussions, setDiscussions] = useState<DiscussionResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    discussionsApi
      .list(slug)
      .then((res) => {
        if (!cancelled) setDiscussions(res.data);
      })
      .catch((err) => {
        if (!cancelled) setError(getApiErrorMessage(err, t('problem.discussError')));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  function handlePosted(discussion: DiscussionResponse) {
    setDiscussions((prev) => [discussion, ...prev]);
    push(t('problem.discussPosted'), 'success');
  }

  function handleReplied(parentId: number, reply: DiscussionResponse) {
    setDiscussions((prev) =>
      prev.map((d) =>
        d.id === parentId ? { ...d, replies: [...(d.replies ?? []), reply] } : d,
      ),
    );
    push(t('problem.discussPosted'), 'success');
  }

  function handleDeleted(id: number, parentId: number | null) {
    if (parentId == null) {
      setDiscussions((prev) => prev.filter((d) => d.id !== id));
    } else {
      setDiscussions((prev) =>
        prev.map((d) =>
          d.id === parentId
            ? { ...d, replies: (d.replies ?? []).filter((r) => r.id !== id) }
            : d,
        ),
      );
    }
    push(t('problem.discussDeleted'), 'success');
  }

  function handleLiked(id: number, parentId: number | null, likesCount: number, likedByMe: boolean) {
    const update = (d: DiscussionResponse) =>
      d.id === id ? { ...d, likesCount, likedByMe } : d;

    if (parentId == null) {
      setDiscussions((prev) => prev.map(update));
    } else {
      setDiscussions((prev) =>
        prev.map((d) =>
          d.id === parentId ? { ...d, replies: (d.replies ?? []).map(update) } : d,
        ),
      );
    }
  }

  if (loading) {
    return (
      <div className="py-12 text-center text-sm text-content-muted">
        {t('common.loading')}
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-12 text-center text-sm text-error">
        {t('problem.discussError')}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 p-4">
      {/* Post form */}
      <PostForm
        slug={slug}
        parentId={null}
        onPosted={handlePosted}
        placeholder={t('problem.discussPlaceholder')}
      />

      {/* Empty state */}
      {discussions.length === 0 && (
        <p className="py-8 text-center text-sm text-content-muted">
          {t('problem.discussEmpty')}
        </p>
      )}

      {/* Comment list */}
      <div className="flex flex-col gap-4">
        {discussions.map((d) => (
          <CommentCard
            key={d.id}
            discussion={d}
            parentId={null}
            slug={slug}
            currentUserId={user?.id ?? null}
            lang={lang}
            onDeleted={handleDeleted}
            onLiked={handleLiked}
            onReplied={handleReplied}
          />
        ))}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  PostForm                                                                    */
/* -------------------------------------------------------------------------- */

interface PostFormProps {
  slug: string;
  parentId: number | null;
  placeholder: string;
  onPosted?: (d: DiscussionResponse) => void;
  onCancel?: () => void;
  autoFocus?: boolean;
}

function PostForm({ slug, parentId, placeholder, onPosted, onCancel, autoFocus }: PostFormProps) {
  const { t } = useTranslation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const push = useToastStore((s) => s.push);
  const [body, setBody] = useState('');
  const [posting, setPosting] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (autoFocus) textareaRef.current?.focus();
  }, [autoFocus]);

  if (!isAuthenticated) {
    return (
      <div className="rounded-lg border border-line bg-surface-secondary p-4 text-sm text-content-secondary">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-accent hover:underline">
          <LogIn className="h-3.5 w-3.5" />
          {t('problem.discussLogin')}
        </Link>
      </div>
    );
  }

  async function submit() {
    if (!body.trim()) return;
    setPosting(true);
    try {
      const res = await discussionsApi.create(slug, body.trim(), parentId);
      setBody('');
      onPosted?.(res.data);
    } catch (err) {
      push(getApiErrorMessage(err, t('problem.discussPostError')), 'error');
    } finally {
      setPosting(false);
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <textarea
        ref={textareaRef}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder={placeholder}
        rows={parentId == null ? 4 : 3}
        className="w-full resize-none rounded-lg border border-line bg-surface px-3 py-2 text-sm text-content placeholder:text-content-muted focus:outline-none focus:ring-2 focus:ring-accent/40"
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) submit();
        }}
      />
      <div className="flex items-center justify-end gap-2">
        {onCancel && (
          <button
            onClick={onCancel}
            className="btn btn-ghost px-3 py-1.5 text-xs"
          >
            {t('common.cancel')}
          </button>
        )}
        <button
          onClick={submit}
          disabled={posting || !body.trim()}
          className="btn btn-primary px-3 py-1.5 text-xs"
        >
          {posting ? '…' : t('problem.discussPost')}
        </button>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  CommentCard                                                                 */
/* -------------------------------------------------------------------------- */

interface CommentCardProps {
  discussion: DiscussionResponse;
  parentId: number | null;
  slug: string;
  currentUserId: number | null;
  lang: Lang;
  onDeleted: (id: number, parentId: number | null) => void;
  onLiked: (id: number, parentId: number | null, count: number, liked: boolean) => void;
  onReplied: (parentId: number, reply: DiscussionResponse) => void;
}

function CommentCard({
  discussion,
  parentId,
  slug,
  currentUserId,
  lang,
  onDeleted,
  onLiked,
  onReplied,
}: CommentCardProps) {
  const { t } = useTranslation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const push = useToastStore((s) => s.push);
  const [replying, setReplying] = useState(false);
  const [showReplies, setShowReplies] = useState(false);
  const [liking, setLiking] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isOwner = currentUserId === discussion.author.id;
  const replyCount = discussion.replies?.length ?? 0;
  const isReply = parentId != null;

  async function handleLike() {
    if (!isAuthenticated || liking) return;
    setLiking(true);
    try {
      const res = await discussionsApi.like(discussion.id);
      onLiked(discussion.id, parentId, res.data.likesCount, res.data.likedByMe);
    } catch (err) {
      push(getApiErrorMessage(err, t('problem.discussPostError')), 'error');
    } finally {
      setLiking(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm(t('problem.discussDeleteConfirm'))) return;
    setDeleting(true);
    try {
      await discussionsApi.delete(discussion.id);
      onDeleted(discussion.id, parentId);
    } catch (err) {
      push(getApiErrorMessage(err, t('problem.discussPostError')), 'error');
      setDeleting(false);
    }
  }

  return (
    <div className={cn('flex gap-3', isReply && 'ml-6 border-l-2 border-line pl-4')}>
      {/* Avatar placeholder */}
      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-surface-secondary text-xs font-medium text-content-secondary uppercase">
        {(discussion.author.displayName ?? discussion.author.username).charAt(0)}
      </div>

      <div className="min-w-0 flex-1">
        {/* Header */}
        <div className="flex flex-wrap items-center gap-2 text-xs text-content-muted">
          <span className="font-medium text-content">
            {discussion.author.displayName ?? discussion.author.username}
          </span>
          <span>·</span>
          <span>{timeAgo(discussion.createdAt, lang)}</span>
        </div>

        {/* Body */}
        <p className="mt-1 whitespace-pre-wrap break-words text-sm text-content">
          {discussion.body}
        </p>

        {/* Actions */}
        <div className="mt-2 flex flex-wrap items-center gap-3">
          {/* Like */}
          <button
            onClick={handleLike}
            disabled={liking || !isAuthenticated}
            className={cn(
              'inline-flex items-center gap-1 text-xs transition-colors',
              discussion.likedByMe
                ? 'text-error'
                : 'text-content-muted hover:text-content',
              !isAuthenticated && 'cursor-default',
            )}
            title={t('problem.discussLike')}
          >
            <Heart className={cn('h-3.5 w-3.5', discussion.likedByMe && 'fill-current')} />
            {discussion.likesCount > 0 && <span>{discussion.likesCount}</span>}
          </button>

          {/* Reply (only on top-level) */}
          {!isReply && isAuthenticated && (
            <button
              onClick={() => setReplying((v) => !v)}
              className="inline-flex items-center gap-1 text-xs text-content-muted transition-colors hover:text-content"
            >
              <MessageSquare className="h-3.5 w-3.5" />
              {t('problem.discussReply')}
            </button>
          )}

          {/* Delete */}
          {isOwner && (
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="inline-flex items-center gap-1 text-xs text-content-muted transition-colors hover:text-error"
            >
              <Trash2 className="h-3.5 w-3.5" />
              {t('problem.discussDelete')}
            </button>
          )}
        </div>

        {/* Reply form */}
        {replying && (
          <div className="mt-3">
            <PostForm
              slug={slug}
              parentId={discussion.id}
              placeholder={t('problem.discussPlaceholder')}
              autoFocus
              onPosted={(reply) => {
                onReplied(discussion.id, reply);
                setReplying(false);
                setShowReplies(true);
              }}
              onCancel={() => setReplying(false)}
            />
          </div>
        )}

        {/* Replies toggle */}
        {replyCount > 0 && (
          <div className="mt-3">
            <button
              onClick={() => setShowReplies((v) => !v)}
              className="inline-flex items-center gap-1 text-xs text-accent hover:underline"
            >
              <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', showReplies && 'rotate-180')} />
              {showReplies
                ? t('problem.discussRepliesHide')
                : t('problem.discussRepliesShow', { n: replyCount })}
            </button>

            {showReplies && (
              <div className="mt-2 flex flex-col gap-3">
                {discussion.replies.map((reply) => (
                  <CommentCard
                    key={reply.id}
                    discussion={reply}
                    parentId={discussion.id}
                    slug={slug}
                    currentUserId={currentUserId}
                    lang={lang}
                    onDeleted={onDeleted}
                    onLiked={onLiked}
                    onReplied={onReplied}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
